import React, { useState, useEffect } from 'react';
import { useTheme, getT, getBrandColors} from '../ThemeContext';
import PageHeaderIcon from "../components/PageHeaderIcon";
import { PrimaryButton, GhostButton } from "../components/ThemedButton";
import API_BASE from '../apiConfig';
import { getSessionUser } from '../utils/session';
import { useDateTimeFormat } from '../utils/formatters';
import { FaCalendarCheck, FaCheckCircle, FaExclamationCircle, FaTimes, FaPlus, FaSave, FaTrash } from 'react-icons/fa';

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";
const DEFAULT_ROWS = 11;

const PAY_COLORS = {
  COMPLETED: { bg: 'rgba(52,211,153,0.12)', text: getBrandColors().green, border: 'rgba(52,211,153,0.30)' },
  PARTIALLY_REFUNDED: { bg: 'rgba(251,191,36,0.12)', text: '#f59e0b', border: 'rgba(251,191,36,0.30)' },
  REFUNDED: { bg: 'rgba(248,113,113,0.12)', text: getBrandColors().red, border: 'rgba(248,113,113,0.30)' },
  FAILED: { bg: 'rgba(248,113,113,0.12)', text: getBrandColors().red, border: 'rgba(248,113,113,0.30)' },
  PENDING: { bg: 'rgba(156,163,175,0.12)', text: '#9ca3af', border: 'rgba(156,163,175,0.30)' },
};

// Rows carry a stable _uid so React keys survive add/remove/reorder — without it,
// index-based keys make previously-filled rows render blank when rows shift.
let _rowSeq = 0;
const mkRow = (student_id = null, dateIds = []) => ({ _uid: `r${++_rowSeq}`, student_id, dateIds });
const emptyRows = (n) => Array.from({ length: n }, () => mkRow());

const TournamentScheduler = ({ embedded = false, extTournamentId = '', extAgeCategoryId = '' } = {}) => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDate } = useDateTimeFormat();
  const user = getSessionUser();

  const [tournaments, setTournaments] = useState([]);
  const [tournamentId, setTournamentId] = useState('');
  const [tournament, setTournament] = useState(null);

  const [ageCategoryId, setAgeCategoryId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [eligibleStudents, setEligibleStudents] = useState([]); // dropdown source
  const [studentInfo, setStudentInfo] = useState({}); // id -> {names, parent, phone}
  const [savedRows, setSavedRows] = useState([]);
  const [rows, setRows] = useState(emptyRows(DEFAULT_ROWS));

  const [draftInfo, setDraftInfo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [skipped, setSkipped] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: 'asc' }); // sort for the scheduled (saved) roster

  const allDateIds = (tournament?.dates || []).map(d => d.tournament_date_id);

  // Prefill from Cost Planner hand-off
  useEffect(() => {
    const prefillId = sessionStorage.getItem('scheduler_prefill_tournament_id');
    if (!prefillId) return;
    sessionStorage.removeItem('scheduler_prefill_tournament_id');
    (async () => {
      try {
        const res = await fetch(`${BASE}/tournament/${prefillId}`);
        const data = await res.json();
        if (data.success && data.tournament) {
          pickTournament({ tournament_id: data.tournament.tournament_id, tournament_name: data.tournament.tournament_name, status: data.tournament.status });
        }
      } catch (err) { console.error(err); }
    })();
    // eslint-disable-next-line
  }, []);

  // Load the tournament list for the dropdown (exclude finished/cancelled).
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/tournament`);
        const data = await res.json();
        if (data.success) setTournaments((data.tournaments || []).filter(t => t.status !== 'CANCELLED' && t.status !== 'COMPLETED'));
      } catch (err) { console.error(err); }
    })();
  }, []);

  const onTournamentChange = (id) => {
    if (!id) { setTournamentId(''); setTournament(null); resetRoster(); setAgeCategoryId(''); return; }
    const t = tournaments.find(x => String(x.tournament_id) === String(id));
    if (t) pickTournament(t);
  };

  const onCategoryChange = (id) => {
    setDateFrom(''); setDateTo('');
    if (!id) { setAgeCategoryId(''); resetRoster(); return; }
    const cat = (tournament?.age_categories || []).find(c => String(c.age_category_id) === String(id));
    if (cat) pickCategory(cat);
  };

  const applyFilters = () => refreshSaved(dateFrom, dateTo);

  // Embedded (hub) mode: selection comes from the shared top filter. Load the
  // tournament directly by id (it may be one the standalone list filters out).
  useEffect(() => {
    if (embedded && extTournamentId) pickTournament({ tournament_id: extTournamentId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, extTournamentId]);
  // Apply the category only once the tournament detail has loaded — otherwise
  // age_categories is empty and the roster never appears (the tab-switch bug).
  useEffect(() => {
    if (!embedded || !extAgeCategoryId || !tournament) return;
    if (String(ageCategoryId) === String(extAgeCategoryId)) return;
    const cat = (tournament.age_categories || []).find(c => String(c.age_category_id) === String(extAgeCategoryId));
    if (cat) pickCategory(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, extAgeCategoryId, tournament]);

  const resetRoster = () => { setSavedRows([]); setEligibleStudents([]); setStudentInfo({}); setRows(emptyRows(DEFAULT_ROWS)); };

  const pickTournament = async (t) => {
    setTournamentId(t.tournament_id);
    setAgeCategoryId('');
    resetRoster();
    setError(''); setSuccess(''); setSkipped([]); setDraftInfo(null);

    try {
      const res = await fetch(`${BASE}/tournament/${t.tournament_id}`);
      const data = await res.json();
      if (!data.success) { setError(data.message || 'Failed to load tournament'); return; }
      setTournament(data);

      const draftRes = await fetch(`${BASE}/tournament/${t.tournament_id}/schedule-draft?user_id=${user.user_id}`);
      const draftData = await draftRes.json();
      if (draftData.success && draftData.draft) {
        const d = draftData.draft;
        const cat = data.age_categories.find(c => c.age_category_id === d.age_category_id);
        if (cat) {
          setAgeCategoryId(String(cat.age_category_id));
          await loadCategoryData(t.tournament_id, cat.age_category_id, d.roster || []);
        }
        setDraftInfo(d.updated_at);
      }
    } catch (err) { setError(err.message); }
  };

  const pickCategory = async (cat) => {
    setAgeCategoryId(String(cat.age_category_id));
    setError(''); setSuccess(''); setSkipped([]);
    await loadCategoryData(tournamentId, cat.age_category_id, []);
  };

  const loadCategoryData = async (tId, catId, draftRoster) => {
    try {
      const [regRes, eligRes] = await Promise.all([
        fetch(`${BASE}/tournament/${tId}/approval?age_category_id=${catId}`),
        fetch(`${BASE}/tournament/${tId}/eligible-students?age_category_id=${catId}`),
      ]);
      const regData = await regRes.json();
      const eligData = await eligRes.json();
      const saved = (regData.success ? (regData.registrations || []) : [])
        .filter(r => !['VOIDED', 'CANCELLED'].includes(r.registration_status));
      const eligible = eligData.success ? (eligData.students || []) : [];
      setSavedRows(saved);
      setEligibleStudents(eligible);

      const info = {};
      eligible.forEach(s => { info[s.student_id] = s; });

      // Empty entry rows fill up to the target squad (DEFAULT_ROWS) minus who's
      // already scheduled — so a nearly-full category shows one spare slot, not 11.
      // Always keep at least one empty row so "Add Student" isn't required to start.
      const emptyTarget = Math.max(1, DEFAULT_ROWS - saved.length);

      if (draftRoster && draftRoster.length) {
        // Restore rows from draft; fetch info for any not in eligible
        const missing = draftRoster.filter(r => !info[r.student_id]);
        await Promise.all(missing.map(async r => {
          try {
            const res = await fetch(`${BASE}/students/${r.student_id}`);
            const dd = await res.json();
            if (dd.student) info[r.student_id] = dd.student;
          } catch (e) { /* skip */ }
        }));
        const restored = draftRoster.map(r => mkRow(r.student_id, r.date_ids || []));
        const targetLen = Math.max(restored.length + 1, DEFAULT_ROWS - saved.length);
        while (restored.length < targetLen) restored.push(mkRow());
        setRows(restored);
      } else {
        setRows(emptyRows(emptyTarget));
      }
      setStudentInfo(info);
    } catch (err) { console.error(err); }
  };

  const refreshSaved = async (from = dateFrom, to = dateTo) => {
    try {
      const params = new URLSearchParams({ age_category_id: ageCategoryId });
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const res = await fetch(`${BASE}/tournament/${tournamentId}/approval?${params.toString()}`);
      const data = await res.json();
      if (data.success) setSavedRows((data.registrations || []).filter(r => !['VOIDED', 'CANCELLED'].includes(r.registration_status)));
    } catch (err) { console.error(err); }
  };

  // ── Row operations ──
  const pickedIds = rows.filter(r => r.student_id).map(r => r.student_id);
  const takenElsewhere = (rowIdx) => new Set([
    ...savedRows.map(s => s.student_id),
    ...rows.filter((_, i) => i !== rowIdx).map(r => r.student_id).filter(Boolean),
  ]);

  const setRowStudent = (idx, studentId) => {
    const sid = studentId ? Number(studentId) : null;
    setRows(prev => prev.map((r, i) => i === idx
      ? { ...r, student_id: sid, dateIds: sid ? [...allDateIds] : [] } // keep _uid; default all days selected
      : r));
  };

  const toggleRowDay = (idx, dateId) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const dateIds = r.dateIds.includes(dateId) ? r.dateIds.filter(d => d !== dateId) : [...r.dateIds, dateId];
      return { ...r, dateIds };
    }));
  };

  const clearRow = (idx) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, student_id: null, dateIds: [] } : r));
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
  const addRow = () => setRows(prev => [...prev, mkRow()]);

  // ── Derived ──
  const filledRows = rows.filter(r => r.student_id && r.dateIds.length > 0);
  const totalPlayers = savedRows.length + filledRows.length;
  const selectedCost = tournament?.costs?.find(c => c.age_category_id === Number(ageCategoryId));
  const poolCost = selectedCost ? Number(selectedCost.tournament_cost_per_player) : 0;
  const squadSize = selectedCost ? (Number(selectedCost.squad_size) || 11) : 11;
  const costPerGame = squadSize > 0 ? poolCost / squadSize : 0;

  // Per-day sign-up counts (column totals). Saved rows + picker rows that actually have a student.
  // Empty picker rows ("Add Student" clicked but nobody chosen) are excluded.
  const savedDayCount = (r) => (r.availability_dates || []).length;                 // row total for a scheduled player
  const dayColumnTotal = (d) =>
    savedRows.filter(r => (r.availability_dates || []).some(x => x.day_number === d.day_number)).length +
    rows.filter(r => r.student_id && r.dateIds.includes(d.tournament_date_id)).length;

  // Sort the scheduled (saved) roster; picker rows always stay below in entry order.
  const toggleSort = (key) => setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  // Persistent affordance: active column shows ▲/▼, others show a faint ⇅ so they read as sortable.
  const sortArrow = (key) => sort.key === key
    ? <span style={{ color: '#60a5fa' }}>{sort.dir === 'asc' ? ' ▲' : ' ▼'}</span>
    : <span style={{ color: T.textMuted, opacity: 0.5 }}> ⇅</span>;
  const sortVal = (r) => {
    switch (sort.key) {
      case 'name': return `${r.first_name} ${r.last_name}`.toLowerCase();
      case 'parent': return (r.parent_name || '').toLowerCase();
      case 'phone': return (r.parent_phone || '').toLowerCase();
      case 'cost': return Number(r.cost_per_game) || 0;
      case 'total': return Number(r.total_cost) || 0;
      case 'refund': return Number(r.refunded_amount) || 0;
      case 'payment': return r.payment_status || '';
      case 'days': return savedDayCount(r);
      default: return 0;
    }
  };
  const sortedSaved = sort.key
    ? [...savedRows].sort((a, b) => {
        const va = sortVal(a), vb = sortVal(b);
        const cmp = va < vb ? -1 : va > vb ? 1 : 0;
        return sort.dir === 'asc' ? cmp : -cmp;
      })
    : savedRows;

  const saveDraft = async () => {
    if (!ageCategoryId) { setError('Select an age category before saving a draft'); return; }
    setError(''); setSuccess('');
    const roster = rows.filter(r => r.student_id).map(r => ({ student_id: r.student_id, date_ids: r.dateIds }));
    try {
      const res = await fetch(`${BASE}/tournament/${tournamentId}/schedule-draft`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, age_category_id: Number(ageCategoryId), roster }),
      });
      const data = await res.json();
      if (data.success) setSuccess('Draft saved — you can resume this roster later.');
      else setError(data.message || 'Failed to save draft');
    } catch (err) { setError(err.message); }
  };

  const saveSchedule = async () => {
    if (!ageCategoryId) { setError('Select an age category'); return; }
    if (filledRows.length === 0) { setError('Pick at least one student and keep at least one day selected'); return; }
    setLoading(true); setError(''); setSuccess(''); setSkipped([]);
    try {
      const res = await fetch(`${BASE}/tournament/${tournamentId}/schedule-team`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.user_id,
          age_category_id: Number(ageCategoryId),
          players: filledRows.map(r => ({ student_id: r.student_id, date_ids: r.dateIds })),
          terms_accepted: true,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(data.message);
        setSkipped(data.skipped || []);
        setDraftInfo(null);
        // Move the players that were actually created into the saved (green) roster,
        // but preserve the row layout the admin set up — only drop the rows that got
        // scheduled. Rows the backend skipped stay so they can be fixed and re-saved.
        const createdIds = new Set((data.created || []).map(c => c.student_id));
        setRows(prev => {
          const remaining = prev.filter(r => !(r.student_id && createdIds.has(r.student_id)));
          return remaining.length ? remaining : emptyRows(1);
        });
        await refreshSaved();
        await refreshEligible();
      } else {
        setError(data.message || data.detail || 'Error scheduling team');
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const refreshEligible = async () => {
    try {
      const res = await fetch(`${BASE}/tournament/${tournamentId}/eligible-students?age_category_id=${ageCategoryId}`);
      const data = await res.json();
      if (data.success) {
        const eligible = data.students || [];
        setEligibleStudents(eligible);
        setStudentInfo(prev => { const info = { ...prev }; eligible.forEach(s => { info[s.student_id] = s; }); return info; });
      }
    } catch (err) { console.error(err); }
  };

  const voidRow = async (row) => {
    if (!window.confirm(`Remove ${row.first_name} ${row.last_name} from this schedule? They'll be selectable again in the dropdown.`)) return;
    try {
      const res = await fetch(`${BASE}/tournament/registrations/${row.registration_id}/void`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id }),
      });
      const data = await res.json();
      if (data.success) {
        setSavedRows(prev => prev.filter(r => r.registration_id !== row.registration_id)); // instant drop
        setSuccess(`${row.first_name} ${row.last_name} removed from the schedule.`);
        refreshEligible();
      } else setError(data.message || data.detail || 'Could not remove');
    } catch (err) { setError(err.message); }
  };

  // ── Styles ──
  const glassCard = { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, border: T.glassBdr, borderRadius: '14px', boxShadow: T.glassSh, padding: '18px 22px', boxSizing: 'border-box', marginBottom: '14px' };
  const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textLabel, fontFamily: FONT };
  const inputStyle = { border: `1.5px solid ${T.inputBdr}`, borderRadius: '8px', padding: '8px 12px', background: T.inputBg, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, width: '100%', boxSizing: 'border-box', outline: 'none', fontSize: '13px', color: T.inputText, fontFamily: FONT };
  const th = { padding: '9px 10px', fontWeight: 800, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: FONT, color: T.textLabel, whiteSpace: 'nowrap' };
  const td = { padding: '7px 10px', fontSize: '12.5px', fontFamily: FONT, color: T.textPrimary, verticalAlign: 'middle' };
  const rowSelect = { width: '100%', minWidth: '150px', padding: '7px 8px', border: `1.5px solid ${T.inputBdr}`, borderRadius: '7px', background: T.inputBg, color: T.inputText, fontSize: '12.5px', outline: 'none', fontFamily: FONT };

  const dates = tournament?.dates || [];
  const chk = (checked, onChange, disabled) => (
    <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange}
      style={{ width: 16, height: 16, cursor: disabled ? 'default' : 'pointer', accentColor: getBrandColors().yellow }} />
  );

  return (
    <div style={embedded ? { padding: 0, boxSizing: 'border-box' } : { minHeight: '100dvh', padding: '18px 26px 40px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: embedded ? '8px' : '16px' }}>
        {embedded ? <div /> : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <PageHeaderIcon icon={<FaCalendarCheck size={19} color={getBrandColors().blueDark} />} />
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 800, color: T.textPrimary, margin: 0, fontFamily: FONT }}>Tournament Scheduler</h1>
            <p style={{ fontSize: '12px', color: T.textMuted, margin: '2px 0 0', fontFamily: FONT }}>Pick players from the dropdown — all days default to selected; untick any they can't play</p>
          </div>
        </div>
        )}
        {tournament && ageCategoryId && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <GhostButton onClick={addRow}><FaPlus size={10} /> Add Student</GhostButton>
            <GhostButton onClick={saveDraft}><FaSave size={11} /> Save Draft</GhostButton>
          </div>
        )}
      </div>

      {error && (
        <div style={{ display: 'flex', gap: '10px', padding: '10px 14px', borderRadius: '9px', marginBottom: '14px', fontSize: '12.5px', fontWeight: 600, fontFamily: FONT, background: 'rgba(248,113,113,0.12)', color: getBrandColors().red, border: '1px solid rgba(248,113,113,0.30)' }}>
          <FaExclamationCircle size={14} /><span>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ marginBottom: '14px', fontFamily: FONT }}>
          <div style={{ display: 'flex', gap: '10px', fontSize: '12.5px', fontWeight: 600, background: 'rgba(52,211,153,0.12)', color: getBrandColors().green, border: '1px solid rgba(52,211,153,0.30)', padding: '10px 14px', borderRadius: '9px' }}>
            <FaCheckCircle size={14} /><span>{success}</span>
          </div>
          {skipped.length > 0 && <div style={{ marginTop: '8px', fontSize: '12px', color: T.textMuted }}>Skipped: {skipped.map(s => `#${s.student_id} (${s.reason})`).join('; ')}</div>}
        </div>
      )}

      {/* Filter criteria — hidden in the hub, which supplies the shared top filter */}
      {!embedded && (
      <div style={glassCard}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '14px' }}>
          <div>
            <label style={labelStyle}>Tournament</label>
            <select value={tournamentId} onChange={(e) => onTournamentChange(e.target.value)} style={inputStyle}>
              <option value="">— Choose —</option>
              {tournaments.map(t => (
                <option key={t.tournament_id} value={t.tournament_id}>{t.tournament_name} ({t.status})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Age Category</label>
            <select value={ageCategoryId} onChange={(e) => onCategoryChange(e.target.value)} style={inputStyle} disabled={!tournament}>
              <option value="">— Choose —</option>
              {(tournament?.age_categories || []).map(c => (
                <option key={c.age_category_id} value={c.age_category_id}>{c.category_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} disabled={!ageCategoryId} />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} disabled={!ageCategoryId} />
          </div>
        </div>
        <GhostButton onClick={applyFilters} disabled={!ageCategoryId} style={{ marginTop: '14px' }}>
          Apply Filters
        </GhostButton>
        {draftInfo && <div style={{ marginTop: '12px', fontSize: '12px', color: '#f59e0b', fontFamily: FONT }}>● Draft restored (last saved {draftInfo}) — continue where you left off.</div>}
      </div>
      )}
      {embedded && draftInfo && <div style={{ marginBottom: '10px', fontSize: '12px', color: '#f59e0b', fontFamily: FONT }}>● Draft restored (last saved {draftInfo}) — continue where you left off.</div>}

      {tournament && ageCategoryId && (
        <>
          <div style={glassCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>
                Roster — {totalPlayers} player{totalPlayers === 1 ? '' : 's'}
              </label>
              <div style={{ fontSize: '11.5px', color: T.textMuted, fontFamily: FONT }}>
                Cost/game <strong style={{ color: getBrandColors().yellow }}>${costPerGame.toFixed(2)}</strong> (Total ${poolCost.toFixed(2)} ÷ squad {squadSize})
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${T.inputBdr}` }}>
                    <th style={{ ...th, textAlign: 'center', width: '34px' }}>#</th>
                    <th style={{ ...th, textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('name')}>Student{sortArrow('name')}</th>
                    <th style={{ ...th, textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('parent')}>Parent Name{sortArrow('parent')}</th>
                    <th style={{ ...th, textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('phone')}>Phone{sortArrow('phone')}</th>
                    {dates.map(d => (
                      <th key={d.tournament_date_id} style={{ ...th, textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', fontWeight: 800, color: getBrandColors().yellow }} title="Players signed up this day">{dayColumnTotal(d)}</div>
                        <div>Day {d.day_number}</div>
                        <div style={{ fontSize: '9.5px', color: T.textMuted, fontWeight: 700 }}>{fmtDate(d.tournament_date)}</div>
                      </th>
                    ))}
                    <th style={{ ...th, textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('days')}>Days{sortArrow('days')}</th>
                    <th style={{ ...th, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('cost')}>Cost/Game{sortArrow('cost')}</th>
                    <th style={{ ...th, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('total')}>Amount{sortArrow('total')}</th>
                    <th style={{ ...th, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('refund')}>Refund{sortArrow('refund')}</th>
                    <th style={{ ...th, textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('payment')}>Payment{sortArrow('payment')}</th>
                    <th style={{ ...th, textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Already scheduled (read-only) */}
                  {sortedSaved.map((r, idx) => {
                    const pc = PAY_COLORS[r.payment_status] || PAY_COLORS.PENDING;
                    const canRefund = r.payment_status === 'COMPLETED' || r.payment_status === 'PARTIALLY_REFUNDED';
                    const days = new Set((r.availability_dates || []).map(d => d.day_number));
                    return (
                      <tr key={`saved-${r.registration_id}`} style={{ background: 'rgba(52,211,153,0.05)', borderBottom: `1px solid ${T.inputBdr}` }}>
                        <td style={{ ...td, textAlign: 'center', color: T.textMuted, fontWeight: 700 }}>{idx + 1}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{r.first_name} {r.last_name}</td>
                        <td style={td}>{r.parent_name}</td>
                        <td style={td}>{r.parent_phone}</td>
                        {dates.map(d => (
                          <td key={d.tournament_date_id} style={{ ...td, textAlign: 'center' }}>{chk(days.has(d.day_number), () => {}, true)}</td>
                        ))}
                        <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{days.size}</td>
                        <td style={{ ...td, textAlign: 'right' }}>${Number(r.cost_per_game).toFixed(2)}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {(() => {
                            const total = Number(r.total_cost) || 0;
                            const refunded = Number(r.refunded_amount) || 0;
                            const net = Math.max(0, total - refunded);
                            return refunded > 0
                              ? (<><span style={{ textDecoration: 'line-through', color: T.textMuted, fontWeight: 500, marginRight: 5 }}>${total.toFixed(2)}</span><span>${net.toFixed(2)}</span></>)
                              : (<span>${total.toFixed(2)}</span>);
                          })()}
                        </td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {Number(r.refunded_amount) > 0
                            ? <span style={{ color: '#ef4444', fontWeight: 700 }}>–${Number(r.refunded_amount).toFixed(2)}</span>
                            : <span style={{ color: T.textMuted }}>—</span>}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <span style={{ padding: '3px 9px', borderRadius: '6px', fontSize: '10.5px', fontWeight: 700, background: pc.bg, color: pc.text, border: `1px solid ${pc.border}` }}>{r.payment_status === 'PARTIALLY_REFUNDED' ? 'Partial Refund' : r.payment_status}</span>
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {canRefund ? (
                            <span style={{ color: T.textMuted, fontSize: '11px' }} title="Paid — refund from Tournament Admin Approval">—</span>
                          ) : (
                            <button onClick={() => voidRow(r)} title="Remove from schedule" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', border: '1px solid rgba(248,113,113,0.35)', borderRadius: '6px', background: 'rgba(248,113,113,0.10)', color: getBrandColors().red, fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                              <FaTrash size={9} /> Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Editable picker rows */}
                  {rows.map((r, idx) => {
                    const info = r.student_id ? studentInfo[r.student_id] : null;
                    const total = costPerGame * r.dateIds.length;
                    const taken = takenElsewhere(idx);
                    const options = eligibleStudents.filter(s => !taken.has(s.student_id));
                    return (
                      <tr key={r._uid} style={{ background: r.student_id ? 'rgba(247,206,11,0.06)' : 'transparent', borderBottom: `1px solid ${T.inputBdr}` }}>
                        <td style={{ ...td, textAlign: 'center', color: T.textMuted, fontWeight: 700 }}>{savedRows.length + idx + 1}</td>
                        <td style={td}>
                          <select value={r.student_id || ''} onChange={(e) => setRowStudent(idx, e.target.value)} style={rowSelect}>
                            <option value="">— select student —</option>
                            {options.map(s => (
                              <option key={s.student_id} value={s.student_id}>
                                {s.first_name} {s.last_name}{s.student_code ? ` · ${s.student_code}` : ''} (age {s.age})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={td}>{info?.parent_name || ''}</td>
                        <td style={td}>{info?.parent_phone || ''}</td>
                        {dates.map(d => (
                          <td key={d.tournament_date_id} style={{ ...td, textAlign: 'center' }}>
                            {chk(r.dateIds.includes(d.tournament_date_id), () => toggleRowDay(idx, d.tournament_date_id), !r.student_id)}
                          </td>
                        ))}
                        <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{r.student_id ? r.dateIds.length : '—'}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{r.student_id ? `$${costPerGame.toFixed(2)}` : '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{r.student_id ? `$${total.toFixed(2)}` : '—'}</td>
                        <td style={{ ...td, textAlign: 'right', color: T.textMuted }}>—</td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <span style={{ padding: '3px 9px', borderRadius: '6px', fontSize: '10.5px', fontWeight: 700, background: T.glass, color: T.textMuted, border: `1px solid ${T.inputBdr}` }}>Not scheduled</span>
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                            {r.student_id && <FaTimes size={13} color={T.textMuted} style={{ cursor: 'pointer' }} title="Clear student" onClick={() => clearRow(idx)} />}
                            <FaTrash size={12} color={getBrandColors().red} style={{ cursor: 'pointer' }} title="Remove row" onClick={() => removeRow(idx)} />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {eligibleStudents.length === 0 && (
              <div style={{ marginTop: '12px', fontSize: '12px', color: T.textMuted, fontFamily: FONT }}>
                No Tournament-Ready students found for this category. Mark students “Tournament Ready” in Student Master to list them here.
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <GhostButton onClick={saveDraft}><FaSave size={11} /> Save Draft</GhostButton>
            <PrimaryButton onClick={saveSchedule} disabled={loading || filledRows.length === 0}>
              {loading ? 'Scheduling...' : `Save Schedule (${filledRows.length})`}
            </PrimaryButton>
          </div>
        </>
      )}
    </div>
  );
};

export default TournamentScheduler;
